import { z } from "zod";
import {
  HANDLE_PATTERN,
  HEX_COLOR_PATTERN,
  PAGE_SLUG_PATTERN,
  PHONE_PATTERN,
  hoursEntry,
  href,
  id,
  image,
  requiredText,
  text,
  waNumber,
} from "./primitives";
import { HEADING_CASES, HEADING_TRACKINGS, HEADING_WEIGHTS, RADIUS_PRESETS, THEME_MOTIONS, THEME_TEXTURES, sectionSchema } from "./sections";

export const DEFINITION_VERSION = 1 as const;
export const LOCALES = ["id-ID", "en-US"] as const;

const handle = () => z.string().regex(HANDLE_PATTERN);
export const GOOGLE_VERIFICATION_PATTERN = /^[A-Za-z0-9_-]{10,100}$/;

export const siteSchema = z.strictObject({
  name: requiredText(80),
  tagline: text(160).optional(),
  industry: id(),
  locale: z.enum(LOCALES),
  logo: image().optional(),
  contact: z.strictObject({
    whatsapp: waNumber().optional(),
    phone: z.string().regex(PHONE_PATTERN).optional(),
    email: z.email().max(254).optional(),
    address: text(300).optional(),
    city: text(80).optional(),
    mapsUrl: href().optional(),
    geo: z
      .strictObject({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
      .optional(),
  }),
  social: z.strictObject({
    instagram: handle().optional(),
    tiktok: handle().optional(),
    facebook: handle().optional(),
    youtube: handle().optional(),
  }),
  hours: z.array(hoursEntry()).max(7).optional(),
  whatsapp: z.strictObject({ defaultMessage: text(300).optional() }).optional(),
  /** Google Search Console HTML-tag token: rendered as <meta name="google-site-verification">. */
  googleVerification: z.string().regex(GOOGLE_VERIFICATION_PATTERN).optional(),
});


export type ThemeOverrides = NonNullable<z.infer<typeof themeRefSchema>["overrides"]>;

export const themeRefSchema = z.strictObject({
  id: id(),
  version: z.number().int().min(1),
  overrides: z
    .strictObject({
      primary: z.string().regex(HEX_COLOR_PATTERN).optional(),
      fontPair: id().optional(),
      /** Page background; surface, border and muted text are derived from it and `text`. */
      background: z.string().regex(HEX_COLOR_PATTERN).optional(),
      text: z.string().regex(HEX_COLOR_PATTERN).optional(),
      accent: z.string().regex(HEX_COLOR_PATTERN).optional(),
      headingCase: z.enum(HEADING_CASES).optional(),
      headingWeight: z.number().refine((n) => (HEADING_WEIGHTS as readonly number[]).includes(n)).optional(),
      headingTracking: z.enum(HEADING_TRACKINGS).optional(),
      radius: z.enum(RADIUS_PRESETS).optional(),
      texture: z.enum(THEME_TEXTURES).optional(),
      /** Site-wide motion style, over the design's own. */
      motion: z.enum(THEME_MOTIONS).optional(),
    })
    .optional(),
});

export const pageSchema = z.strictObject({
  id: id(),
  slug: z.string().regex(PAGE_SLUG_PATTERN),
  title: requiredText(80),
  seo: z.strictObject({
    title: text(70),
    description: text(170),
    ogImage: image().optional(),
    noindex: z.boolean().optional(),
  }),
  /** Left out of the top menu; still public, reached by its link (a button, a QR code). Ignored on the home page. */
  hideFromNav: z.boolean().optional(),
  sections: z.array(sectionSchema).max(40),
});

const baseDefinitionSchema = z.strictObject({
  version: z.literal(DEFINITION_VERSION),
  site: siteSchema,
  theme: themeRefSchema,
  pages: z.array(pageSchema).min(1).max(20),
  meta: z
    .strictObject({
      source: z.enum(["ai", "template", "manual"]).optional(),
      templateId: id().optional(),
      templateVersion: z.number().int().min(1).optional(),
    })
    .optional(),
});

export type WebsiteDefinition = z.infer<typeof baseDefinitionSchema>;
export type PageNode = z.infer<typeof pageSchema>;
export type SiteInfo = z.infer<typeof siteSchema>;
export type ThemeRef = z.infer<typeof themeRefSchema>;

/** Pages listed in the top menu, home first. The home page is always listed. */
export function menuPages(def: Pick<WebsiteDefinition, "pages">): PageNode[] {
  return def.pages.filter((p) => p.slug === "/" || !p.hideFromNav).sort((a, b) => (a.slug === "/" ? -1 : b.slug === "/" ? 1 : 0));
}

export interface SemanticIssue {
  path: (string | number)[];
  code:
    | "missing_home_page"
    | "duplicate_page_id"
    | "duplicate_page_slug"
    | "reserved_page_slug"
    | "duplicate_section_id"
    | "duplicate_anchor"
    | "multiple_singleton"
    | "singleton_position";
  message: string;
}

/** Page addresses taken by the store's own pages (product, checkout, order). */
export const RESERVED_PAGE_SLUGS: readonly string[] = ["/produk", "/checkout", "/pesanan"];

/**
 * Cross-field rules JSON Schema cannot express. The Go API implements the exact same rules
 * (apps/api/internal/definition/semantic.go); shared fixtures in /fixtures keep them in sync.
 *
 * 1. Exactly one page has slug "/".
 * 2. Page ids and page slugs are unique.
 * 3. Section ids are unique across the whole definition.
 * 4. Section anchors are unique within a page.
 * 5. At most one navbar and one footer per page; navbar must be first, footer must be last.
 * 6. Page slugs used by the store's own pages (RESERVED_PAGE_SLUGS) are not available.
 */
export function semanticIssues(def: WebsiteDefinition): SemanticIssue[] {
  const issues: SemanticIssue[] = [];
  const pageIds = new Set<string>();
  const slugs = new Set<string>();
  const sectionIds = new Set<string>();

  if (def.pages.filter((p) => p.slug === "/").length !== 1) {
    issues.push({ path: ["pages"], code: "missing_home_page", message: "Harus ada tepat satu halaman beranda (/)." });
  }

  def.pages.forEach((page, pi) => {
    if (pageIds.has(page.id)) {
      issues.push({ path: ["pages", pi, "id"], code: "duplicate_page_id", message: `ID halaman "${page.id}" dipakai lebih dari sekali.` });
    }
    pageIds.add(page.id);
    if (slugs.has(page.slug)) {
      issues.push({ path: ["pages", pi, "slug"], code: "duplicate_page_slug", message: `Alamat halaman "${page.slug}" dipakai lebih dari sekali.` });
    }
    slugs.add(page.slug);
    if (RESERVED_PAGE_SLUGS.includes(page.slug)) {
      issues.push({ path: ["pages", pi, "slug"], code: "reserved_page_slug", message: `Alamat halaman "${page.slug}" dipakai untuk Toko. Pilih alamat lain.` });
    }

    const anchors = new Set<string>();
    const last = page.sections.length - 1;
    let navbars = 0;
    let footers = 0;
    page.sections.forEach((section, si) => {
      const path = ["pages", pi, "sections", si];
      if (sectionIds.has(section.id)) {
        issues.push({ path: [...path, "id"], code: "duplicate_section_id", message: `ID section "${section.id}" dipakai lebih dari sekali.` });
      }
      sectionIds.add(section.id);
      if (anchors.has(section.anchor)) {
        issues.push({ path: [...path, "anchor"], code: "duplicate_anchor", message: `Anchor "#${section.anchor}" dipakai lebih dari sekali di halaman ini.` });
      }
      anchors.add(section.anchor);
      if (section.type === "navbar") {
        navbars++;
        if (si !== 0) issues.push({ path, code: "singleton_position", message: "Navbar harus berada paling atas." });
      }
      if (section.type === "footer") {
        footers++;
        if (si !== last) issues.push({ path, code: "singleton_position", message: "Footer harus berada paling bawah." });
      }
    });
    if (navbars > 1) issues.push({ path: ["pages", pi], code: "multiple_singleton", message: "Hanya boleh ada satu navbar per halaman." });
    if (footers > 1) issues.push({ path: ["pages", pi], code: "multiple_singleton", message: "Hanya boleh ada satu footer per halaman." });
  });

  return issues;
}

/** Structural schema only (what is exported to JSON Schema). */
export const websiteDefinitionStructureSchema = baseDefinitionSchema;

/** Full schema: structure + semantic rules. Use this for parsing. */
export const websiteDefinitionSchema = baseDefinitionSchema.superRefine((def, ctx) => {
  for (const issue of semanticIssues(def)) {
    ctx.addIssue({ code: "custom", path: issue.path, message: issue.message, params: { code: issue.code } });
  }
});
